import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-conversion.jpg";
import cover640 from "@/assets/cover-pillar-conversion-640.webp";
import cover960 from "@/assets/cover-pillar-conversion-960.webp";
import cover1280 from "@/assets/cover-pillar-conversion-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

const PATH = "/resources/shopify-conversion-rate-optimization-playbook";
const ALT = "/es/recursos/playbook-cro-shopify";
const TITLE = "Shopify CRO Playbook: Find and Fix Conversion Leaks (2026)";
const DESC =
  "A practical Shopify CRO framework: validate tracking, segment the funnel, fix technical and UX leaks first, and test only where real alternatives exist.";
const PUBLISHED = "2026-05-19T09:00:00.000Z";
const MODIFIED = "2026-10-28T09:00:00.000Z";

const SECTIONS = [
  {
    id: "why-cro-advice-fails",
    heading: "Why most Shopify CRO advice is wrong for $1M+ stores",
    blocks: [
      "Most CRO content on the internet is written for stores doing $30k/month, where any button color change moves the needle because the sample size is tiny. Once you're past $1M ARR, the lever shape is completely different. Tiny tweaks don't move the meter because your floor is already high. What moves the meter is structural, the page architecture, the merchandising logic, the cart and checkout flow, and the trust signals.",
      "This playbook is the CRO operating system we run on $1M–$50M Shopify Plus stores. It assumes you already have working analytics, real traffic and a brand someone has heard of. If you're earlier than that, ship more product first, then come back.",
    ],
  },
  {
    id: "cro-operating-system",
    heading: "The CRO operating system: research → hypothesis → test → ship",
    blocks: [
      "Random tests on random pages cancel each other out. A real CRO program runs on a four-step loop, every week, forever.",
      "### 1. Research",
      "Pull funnel analytics, session recordings (Hotjar, Microsoft Clarity), search queries, support tickets, returns reasons, and on-site survey data. The research output is a ranked list of friction points, not opinions.",
      "### 2. Hypothesis",
      "Every hypothesis is one sentence: \"If we [change X] on [page Y], [metric Z] will improve by [N%] because [evidence A].\" Anything fuzzier than that is a wish, not a hypothesis.",
      "### 3. Test",
      "Test where you have the traffic to learn. How many tests can run at once depends on each test's required sample size, which comes from your baseline conversion rate, the minimum effect you need to detect and the traffic each test receives, not from revenue alone. Calculate it before committing, then pick the highest-leverage tests and protect them. For test design, sample sizes and when not to test, see our [Shopify A/B testing guide](/blog/shopify-ab-testing).",
      "### 4. Ship",
      "Winners ship to 100%. Losers get documented in your learnings log, that log is the most valuable asset the CRO program builds, more valuable than any single test.",
    ],
  },
  {
    id: "pdp-leverage",
    heading: "PDP: the highest-leverage page on your store",
    blocks: [
      "If you have one hour to spend on CRO this month, spend it on PDPs. Your PDP is where buyers decide whether you're worth the click, and where the average store leaks 15–25% of potential add-to-cart rate.",
      "### Above the fold",
      "- Product title with the actual job-to-be-done, not just the SKU name.",
      "- Hero image at correct size with `fetchpriority` and srcset, slow PDPs lose more than ugly PDPs.",
      "- Price with promotional context (anchor price, member price, savings stack).",
      "- Variant selector that doesn't reset on out-of-stock combinations.",
      "- Add-to-cart with a real label (\"Add, $89\"), not a generic \"ADD\".",
      "### Below the fold",
      "Reviews with photos, comparison table, sizing/fit guidance, return policy and the FAQ that answers the questions your support team gets 50× a week. Make them scannable, buyers don't read, they scan.",
    ],
  },
  {
    id: "cart-and-checkout",
    heading: "Cart and checkout: where money quietly leaks",
    blocks: [
      "Cart and checkout abandonment is rarely about one big issue. It's a dozen small ones that compound. The fix is auditing and removing friction, not adding more upsell modules.",
      "### Cart drawer",
      "- Show line items, price clarity, and any discount logic transparently.",
      "- One upsell offer, not three. More upsells distract from completing the cart.",
      "- Estimated shipping when possible, shipping surprise at checkout is the #1 abandonment driver.",
      "### Checkout",
      "- Use Shopify's new checkout extensibility. Migrate off `checkout.liquid` if you haven't.",
      "- Reduce fields. Every optional field that's not optional is a cost.",
      "- Honor saved Shop Pay vaulting, it's the highest converting payment method by 20–30%.",
      "- Trust signals at the payment step (return policy, security badge, support contact).",
    ],
  },
  {
    id: "search-and-merchandising",
    heading: "Search, navigation and merchandising",
    blocks: [
      "Search converts 3–5× higher than browse. If your search is broken (returns no results, returns wrong results, no synonyms), you're funding paid traffic that could have closed itself.",
      "### Search",
      "Use Shopify Search & Discovery or Algolia. Configure synonyms for your top 100 misspellings. Pipe \"no results\" queries into your weekly merchandising review.",
      "### Navigation",
      "Mega menu over flyout for stores with 100+ SKUs. Group by job-to-be-done, not by internal product taxonomy. Test mobile menu separately, it has different ergonomics.",
      "### Merchandising",
      "PLP sorting matters more than people think. Default sort should be \"likely to convert\" not \"newest.\" Pin hero products. Demote out-of-stock to the back. Re-rank weekly based on inventory and margin.",
    ],
  },
  {
    id: "mobile-is-different",
    heading: "Mobile is not desktop with smaller margins",
    blocks: [
      "Most DTC traffic is mobile. Most CRO testing is done on desktop. That gap is why mobile conversion plateaus.",
      "### Mobile-first audit",
      "- Test every flow on a real mid-tier phone with throttled network, not a desktop emulator.",
      "- Sticky add-to-cart on PDP scroll is one of the highest-leverage mobile patterns.",
      "- One-handed thumb zones, anything important goes within 60% of screen height from the bottom.",
      "- Reduce form fields aggressively. Use Shop Pay, Apple Pay, Google Pay as primary, not secondary.",
      "### Mobile-specific metrics",
      "Track mobile and desktop separately. Their funnels behave differently and improvements in one don't automatically transfer.",
    ],
  },
  {
    id: "metrics-that-matter",
    heading: "The metrics that matter: revenue per session, AOV, repeat rate",
    blocks: [
      "Conversion rate is a vanity metric in isolation. The metrics we run programs against:",
      "### Revenue per session (RPS)",
      "This is the master metric. Captures conversion, AOV and discount discipline in one number. If RPS is up, the program is working.",
      "### Average order value (AOV)",
      "Move with bundles, free shipping thresholds, and upsell at the right moments (cart, not PDP). Test discounts on AOV, not just on conversion.",
      "### Repeat rate / LTV",
      "Most stores' growth is bottlenecked by retention, not acquisition. Post-purchase, email/SMS flows, subscription offers and the first 30-day experience all live here.",
      "### Cost per add (CPA from paid)",
      "If your store's CRO is working, your paid CPA falls. CRO programs that don't move CPA aren't moving the right things.",
    ],
  },
  {
    id: "testing-without-traffic",
    heading: "Running tests when you don't have Optimizely traffic",
    blocks: [
      "Most Shopify stores don't have the traffic to run classical A/B tests at 95% confidence in two weeks. Pretending you do leads to false wins and false losses.",
      "### Pragmatic frameworks",
      "- Sequential testing with Bayesian inference (GrowthBook, Statsig) is more honest at low traffic than fixed-horizon frequentist tests.",
      "- Pre/post analysis with proper holdouts works for irreversible structural changes (new PDP template, new checkout).",
      "- Multi-armed bandits for merchandising-style decisions where you want to shift traffic to the winner mid-test.",
      "### When to skip the test",
      "If a change is qualitatively obviously better (page is faster, copy is clearer, broken feature is fixed), ship it. Don't burn weeks A/B testing a fix you'd ship either way.",
    ],
  },
];

const FAQS = [
  {
    q: "How long until CRO work shows up in revenue?",
    a: "Structural wins (PDP rebuild, checkout migration, cart redesign) usually move RPS within 14–30 days. Test programs take 60–90 days to compound, the value is in the learning cadence, not any single test.",
  },
  {
    q: "Do we need an A/B testing tool?",
    a: "Eventually yes, GrowthBook or Statsig are our defaults on Shopify. But the first 90 days of any CRO program are usually structural fixes that don't need a testing tool. Add the tool when you have the traffic and the program rhythm to use it.",
  },
  {
    q: "What conversion rate should I be at?",
    a: "Median Shopify Plus stores in $1M–$50M ARR sit around 2.0–3.2% on mobile and 3.2–4.5% on desktop. If you're below the median for your category, structural CRO has room to move you. Above the median, programs get incremental.",
  },
  {
    q: "Should I focus on conversion rate or AOV?",
    a: "Track RPS. It captures both. If conversion is below benchmark, prioritize friction removal. If AOV is below benchmark, prioritize bundles, free shipping thresholds, and cart upsells.",
  },
  {
    q: "Can CRO replace paid acquisition?",
    a: "No, but it makes every dollar of paid acquisition stretch further. A 15% lift in RPS on a $5M store funds another 15% of paid traffic at the same blended ROAS.",
  },
];

const RELATED = [
  { label: "Read all conversion guides →", href: "/resources/conversion" },
  { label: "Service: CRO & SEO Cleanup", href: "/shopify-cro" },
  { label: "Pillar: Shopify Plus Agency", href: "/shopify-plus-agency" },
];

export const Route = createFileRoute("/resources/shopify-conversion-rate-optimization-playbook")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Conversion",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Conversion",
        }),
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Conversion", path: "/resources/conversion" },
          { name: "CRO Playbook", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="en"
        path={PATH}
        eyebrow="Conversion"
        title="The Shopify Conversion Rate Optimization Playbook"
        intro="A CRO operating system for $1M+ DTC brands on Shopify Plus: how we research, hypothesize, test and ship, and the templates we use on PDP, cart, checkout and merchandising."
        updated="May 2026"
        readingMinutes={19}
        hubHref="/resources/conversion"
        hubLabel="Browse all conversion guides"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "The Shopify Conversion Rate Optimization Playbook, illustrated zebra cover" }}
        midCTA={{
          eyebrow: "Pulse for CRO",
          title: "Start with a 5-day conversion diagnostic",
          body: "A senior strategist audits your funnel, PDP, cart and checkout against 100+ store benchmarks and sends back a prioritized fix list with revenue estimates.",
          href: "/pulse",
          label: "See Pulse",
        }}
        finalCTA={{
          eyebrow: "Ready to move",
          title: "Get a CRO project scoped in 48 hours",
          body: "Tell us your store, your current RPS and the funnel step that's leaking. We'll send a fixed-scope plan with timelines, deliverables and price.",
          href: "/get-a-quote?source=pillar-conversion",
          label: "Request a scope",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
    </SiteShell>
  ),
});